import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import GridGuideCell from './components/GridGuideCell';

const GridGuideOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
`;

function GridGuide({ breakpoint, gridRef, items, startIndex }) {
  const [guideGrid, setGuideGrid] = useState([]);
  const [gridStyles, setGridStyles] = useState({
    gridTemplateColumns: '',
    gridTemplateRows: '',
    gridGap: '',
  });
  const numColumns = breakpoint.columns || 3;

  const emptyCellBuffer = useMemo(
    () =>
      startIndex === 0
        ? 0
        : (numColumns - (startIndex % numColumns)) % numColumns,
    [startIndex, numColumns]
  );

  const updateGridStyles = useCallback(() => {
    const gridElement = gridRef.current;
    if (gridElement) {
      const computedStyle = window.getComputedStyle(gridElement);
      setGridStyles({
        gridTemplateColumns: computedStyle.gridTemplateColumns,
        gridTemplateRows: computedStyle.gridTemplateRows,
        gridGap: computedStyle.gap || computedStyle.gridGap,
      });
    }
  }, [gridRef]);

  const addUnavailableCell = (updatedItems, index) => {
    updatedItems.splice(index, 0, {
      itemType: 'asset',
      cellsWide: 1,
      cellsTall: 1,
    });
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateGridStyles();
    });
    const gridElement = gridRef.current;
    if (gridElement) {
      resizeObserver.observe(gridElement);
    }
    return () => {
      if (gridElement) {
        resizeObserver.unobserve(gridElement);
      }
    };
  }, [gridRef, updateGridStyles]);

  useEffect(() => {
    const updatedItems = [...items];
    items.forEach((item, index) => {
      if (item.itemType === 'asset' && item.cellsWide > 1) {
        for (let i = 1; i < item.cellsWide; i++) {
          addUnavailableCell(updatedItems, index + i);
        }
      }
    });
    const emptyCells =
      (numColumns - (updatedItems.length % numColumns)) % numColumns;
    for (let i = 0; i < emptyCells; i++) {
      updatedItems.push({
        itemType: 'empty',
        cellsWide: 1,
        cellsTall: 1,
      });
    }
    setGuideGrid(updatedItems);
  }, [items, gridStyles, numColumns]);

  return (
    <GridGuideOverlay
      style={{
        gridTemplateColumns: gridStyles.gridTemplateColumns,
        gridTemplateRows: gridStyles.gridTemplateRows,
        gridGap: gridStyles.gridGap,
      }}
    >
      {guideGrid.map((item, index) => {
        const position = emptyCellBuffer + startIndex + index + 1;
        const row = Math.floor((position - 1) / numColumns) + 1;
        const column = (index % numColumns) + 1;
        return (
          <GridGuideCell
            key={index}
            column={column}
            item={item}
            numColumns={numColumns}
            position={position}
            row={row}
          />
        );
      })}
    </GridGuideOverlay>
  );
}

GridGuide.propTypes = {
  breakpoint: PropTypes.object,
  gridRef: PropTypes.object,
  items: PropTypes.array,
  startIndex: PropTypes.number,
};

export default GridGuide;
